import React, { useState } from 'react';
import { generateArtistSlug } from '../utils/textMeasurement';
import { useShareTracking } from '../hooks/useAnalytics';
import { logger } from '../utils/logger';

interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  playlistUrl?: string;
  shortUrl?: string;
  artistName: string;
  songs: string[];
}

const ShareModal: React.FC<ShareModalProps> = ({ isOpen, onClose, playlistUrl, shortUrl, artistName, songs }) => {
  const [copySuccess, setCopySuccess] = useState(false);
  
  // Debug logging
  logger.debug('ShareModal props:', { playlistUrl, artistName });
  const { trackShareAction } = useShareTracking();


  if (!isOpen) return null;

  const handleCopyLink = async () => {
    // Prefer short URL for easier sharing, fall back to full URL
    const urlToCopy = shortUrl ? `https://${shortUrl}` : playlistUrl;
    if (!urlToCopy) return;
    
    const onSuccess = trackShareAction('copy_link', artistName, urlToCopy);
    
    try {
      await navigator.clipboard.writeText(urlToCopy);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
      onSuccess();
    } catch (error) {
      logger.error('Failed to copy:', error);
    }
  };

  const handleInstagram = async () => {
    const onSuccess = trackShareAction('instagram', artistName, playlistUrl);
    
    try {
      logger.log('Creating Instagram story with template design');
      logger.log('Artist:', artistName);
      logger.log('Songs:', songs);
      
      // Create canvas
      const canvas = document.createElement('canvas');
      canvas.width = 1080;
      canvas.height = 1920;
      const ctx = canvas.getContext('2d');
      
      if (!ctx) {
        logger.error('Failed to get canvas context');
        return;
      }
      
      // Load the background PNG template
      logger.log('Loading Instagram story background...');
      const bgImg = new Image();
      bgImg.src = '/assets/instagram-story-background.png';
      
      await new Promise((resolve, reject) => {
        bgImg.onload = () => {
          logger.log('Background loaded successfully');
          resolve(undefined);
        };
        bgImg.onerror = (err) => {
          logger.error('Failed to load background:', err);
          reject(err);
        };
      });
      
      // Draw background
      ctx.drawImage(bgImg, 0, 0, 1080, 1920);
      
      // Load Permanent Marker font for the artist name
      await document.fonts.load('bold 72px "Permanent Marker"');
      
      // Draw artist name and "SETLIST" title
      ctx.fillStyle = '#000000';
      ctx.textAlign = 'center';
      
      // Dynamically size the title to fit
      let titleFontSize = 72;
      const titleText = `${artistName.toUpperCase()} SETLIST`;
      const maxTitleWidth = 900;
      
      ctx.font = `bold ${titleFontSize}px "Permanent Marker", cursive`;
      while (ctx.measureText(titleText).width > maxTitleWidth && titleFontSize > 40) {
        titleFontSize -= 2;
        ctx.font = `bold ${titleFontSize}px "Permanent Marker", cursive`;
      }
      
      ctx.fillText(titleText, 540, 340);
      
      // Draw underline for artist name only
      const titleWidth = ctx.measureText(titleText).width;
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(540 - titleWidth/2, 355);
      ctx.lineTo(540 + titleWidth/2, 355);
      ctx.stroke();
      
      // Draw songs in the same handwritten style
      ctx.font = 'bold 44px "Permanent Marker", cursive';
      ctx.textAlign = 'center';
      
      const lineHeight = 75; // Slightly reduced line height
      const maxSongs = 11; // Reduce to fit everything without overlap
      const displaySongs = songs.slice(0, maxSongs);
      const maxWidth = 800; // Maximum width for text to fit in white area
      
      // Start songs after the title with more padding
      let yPosition = 450;
      
      displaySongs.forEach((song) => {
        let songName = song.toUpperCase();
        
        // Check if text is too long and truncate if necessary
        if (ctx.measureText(songName).width > maxWidth) {
          while (ctx.measureText(songName + '...').width > maxWidth && songName.length > 10) {
            songName = songName.substring(0, songName.length - 1);
          }
          songName += '...';
        }
        
        ctx.fillText(songName, 540, yPosition);
        yPosition += lineHeight;
      });
      
      // If there are more songs, add the "+X MORE!" text
      if (songs.length > maxSongs) {
        ctx.font = 'bold 48px "Permanent Marker", cursive';
        ctx.fillText(`+${songs.length - maxSongs} MORE!`, 540, yPosition);
        yPosition += lineHeight;
      }
      
      // Prepare URL for display - use short URL if available
      let displayUrl = '';
      
      if (shortUrl) {
        // Use the short URL for display (much more memorable!)
        displayUrl = shortUrl.replace('https://', '').replace('http://', '');
      } else if (playlistUrl && playlistUrl.includes('spotify.com')) {
        // Fallback to Spotify URL if no short URL - keep the full URL!
        displayUrl = playlistUrl
          .replace('https://', '')
          .replace('http://', '');
        
        // Shorten if too long - but keep open.spotify.com
        if (displayUrl.length > 50) {
          const playlistId = displayUrl.split('/playlist/')[1]?.split('?')[0];
          if (playlistId) {
            displayUrl = `open.spotify.com/playlist/${playlistId.substring(0, 8)}...`;
          }
        }
      } else {
        displayUrl = 'festprep.com/playlist';
      }
      
      const fullUrl = displayUrl;
      
      // Button dimensions
      const buttonWidth = 420; // Narrower button width
      const buttonX = (1080 - buttonWidth) / 2;
      const urlLines = Math.ceil(fullUrl.length / 30); // Adjust for larger font
      const buttonHeight = Math.max(100, 70 + (urlLines * 24)); // Dynamic height with more padding
      
      // Position elements with fixed spacing
      const whiteSectionBottom = 1820; // Approximate bottom of white section
      const attributionY = whiteSectionBottom - 70; // Attribution near bottom
      
      // Position button with fixed spacing after songs
      const buttonSpacing = 80; // Reduced space after songs
      const finalButtonY = yPosition + buttonSpacing;
      
      // Draw horizontal rule between setlist and button
      const lineY = yPosition + 50; // Midway between songs and button
      ctx.strokeStyle = 'rgba(0, 0, 0, 0.5)'; // Semi-transparent
      ctx.lineWidth = 2;
      ctx.beginPath();
      const lineWidth = 400; // Half the width
      const lineStartX = (1080 - lineWidth) / 2;
      ctx.moveTo(lineStartX, lineY);
      ctx.lineTo(lineStartX + lineWidth, lineY);
      ctx.stroke();
      
      // Draw button background with proper color
      ctx.save();
      ctx.fillStyle = '#17A34A'; // Green from SVG
      ctx.beginPath();
      ctx.roundRect(buttonX, finalButtonY, buttonWidth, buttonHeight, 17);
      ctx.fill();
      
      // Load and draw Spotify logo
      const spotifyLogo = new Image();
      spotifyLogo.src = '/assets/image.png';
      
      await new Promise((resolve, reject) => {
        spotifyLogo.onload = () => resolve(undefined);
        spotifyLogo.onerror = reject;
      });
      
      // Draw Spotify logo - vertically centered
      const logoSize = 40;
      const logoX = buttonX + 20;
      const logoY = finalButtonY + (buttonHeight / 2) - (logoSize / 2); // Vertically center in button
      
      // Draw the logo
      ctx.drawImage(
        spotifyLogo, 
        logoX, 
        logoY, 
        logoSize, 
        logoSize
      );
      
      // Draw button text - properly vertically centered
      ctx.fillStyle = '#FFFFFF';
      ctx.font = 'bold 26px Arial, sans-serif';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      
      // Draw main text centered at top of button
      ctx.textBaseline = 'top';
      ctx.textAlign = 'center';
      const buttonCenterX = buttonX + buttonWidth / 2;
      ctx.fillText('Open Playlist:', buttonCenterX, finalButtonY + 15);
      ctx.textAlign = 'left'; // Reset for other text
      
      // fullUrl already prepared above
      
      // Use larger font for the URL
      ctx.font = 'bold 20px Arial, sans-serif';
      ctx.textBaseline = 'top';
      
      // Calculate available width for text (button width minus padding)
      const maxTextWidth = buttonWidth - 100; // Adjust for narrower button
      
      // Split URL into multiple lines if needed
      const words = fullUrl.split('/');
      const lines = [];
      let currentLine = words[0]; // Start with domain
      
      for (let i = 1; i < words.length; i++) {
        const testLine = currentLine + '/' + words[i];
        const metrics = ctx.measureText(testLine);
        
        if (metrics.width > maxTextWidth && currentLine !== '') {
          lines.push(currentLine);
          currentLine = '/' + words[i];
        } else {
          currentLine = testLine;
        }
      }
      lines.push(currentLine); // Add the last line
      
      // Draw each line centered in button
      const urlLineHeight = 24;
      const startY = finalButtonY + 50; // More space between title and URL
      ctx.textAlign = 'center';
      const centerX = buttonX + buttonWidth / 2;
      lines.forEach((line, index) => {
        ctx.fillText(line, centerX, startY + (index * urlLineHeight));
      });
      ctx.textAlign = 'left'; // Reset alignment
      
      ctx.textBaseline = 'alphabetic'; // Reset to default
      
      ctx.restore();
      ctx.textAlign = 'center';
      
      // Draw attribution at bottom
      ctx.fillStyle = '#000000';
      ctx.font = '28px Arial, sans-serif'; // Smaller font for attribution
      ctx.textAlign = 'center';
      ctx.fillText('Created by FestPrep.com', 540, attributionY);
      ctx.font = '24px Arial, sans-serif'; // Smaller font
      ctx.fillText('Using Spotify & Setlist.fm APIs', 540, attributionY + 35); // Slightly closer
      
      // Convert to blob and share to Instagram Stories
      canvas.toBlob(async (blob) => {
        if (blob) {
          const fileSlug = generateArtistSlug(artistName);
          const fileName = `${fileSlug}-setlist-festprep.png`;
          
          // Download the image for manual sharing
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = fileName;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
          
          onSuccess();
          
          // Show instructions for Instagram Stories with the short URL
          const shareUrl = shortUrl ? `https://${shortUrl}` : playlistUrl;
          const displayShareUrl = shortUrl || playlistUrl || 'playlist URL';
          alert(`Image downloaded! To share to Instagram:\n\n1. Open Instagram app\n2. Create a new post or story\n3. Select the downloaded image\n4. Add to caption: ${displayShareUrl}\n\nThe short URL (${displayShareUrl}) is easy to type and remember!\nIt will redirect to the Spotify playlist.`);
          
          // Also copy the short URL to clipboard for easy pasting
          if (shareUrl) {
            try {
              await navigator.clipboard.writeText(shareUrl);
            } catch (err) {
              logger.warn('Could not copy URL to clipboard:', err);
            }
          }
        }
      }, 'image/png', 1.0);

    } catch (error) {
      logger.error('Error generating Instagram story:', error);
      alert('Failed to generate Instagram story. Please try again.');
    }
  };

  const socialButtons = [
    {
      id: 'copy-link',
      label: 'Copy Link',
      onClick: handleCopyLink,
      enabled: true,
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
        </svg>
      )
    },
    {
      id: 'instagram',
      label: 'Instagram',
      onClick: handleInstagram,
      enabled: true,
      icon: (
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zM5.838 12a6.162 6.162 0 1112.324 0 6.162 6.162 0 01-12.324 0zM12 16a4 4 0 110-8 4 4 0 010 8zm4.965-10.405a1.44 1.44 0 112.881.001 1.44 1.44 0 01-2.881-.001z"/>
        </svg>
      )
    }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">Share</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex justify-around">
          {socialButtons.map((button) => (
            <div key={button.id} className="flex flex-col items-center">
              <button
                onClick={button.onClick}
                disabled={!button.enabled}
                className={`p-3 rounded-full transition-colors ${
                  button.enabled
                    ? 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                    : 'bg-gray-50 text-gray-300 cursor-not-allowed'
                }`}
              >
                {button.icon}
              </button>
              <span className={`text-xs mt-2 ${button.enabled ? 'text-gray-700' : 'text-gray-400'}`}>
                {button.label}
              </span>
              {!button.enabled && (
                <span className="text-xs text-gray-400">Coming Soon</span>
              )}
            </div>
          ))}
        </div>

        {copySuccess && (
          <div className="mt-4 p-2 bg-green-100 text-green-700 rounded text-center text-sm">
            Link copied to clipboard!
          </div>
        )}
      </div>
    </div>
  );
};

export default ShareModal;